/**
 * PeaceTeamNotes — the "From the Peace team" note card (UX-13 S3
 * extraction from OrganizationDetail; X.6.6-R treatment preserved).
 *
 * The one place the Directory speaks in a colleague's voice rather than a
 * database's (§6 blesses it as the "team voice" surface): sand ground,
 * gold hairline, serif italic body, author + relative time in ink-soft.
 * Type badges wear the §4 trio via NOTE_TYPE_STYLES (Tip=affirm,
 * Warning=caution, General=neutral) at the tight radius. The add-note
 * trigger is the Button primitive (the geometry ruling's pill
 * retirement); edit/delete stay the quiet per-note glyph buttons.
 *
 * The same card serves an organization's notes and a Directory person's.
 * A person also has one older free-text notes field; the page passes it
 * as `standing`, which sits after the dated notes on the same hairline
 * rhythm, so the card stays one card and the empty sentence shows only
 * when there is neither.
 */

import { type ReactNode } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { NOTE_TYPE_STYLES } from '@/components/peace-hub-port/ResourceDetail';
import { HubBrandMark } from '@/components/hub/HubBrandMark';
import { formatRelative } from '@/lib/format';
import { endSentence } from '@/lib/sentence';
import type { TeamNote } from '@/lib/types';

export interface PeaceTeamNotesProps<N extends TeamNote = TeamNote> {
  /** An organization's notes or a person's: the card reads the fields both share. */
  notes: ReadonlyArray<N>;
  /** The listing's name: the organization's, or the person's. */
  providerName: string;
  onAddNote: (() => void) | null;
  onEditNote?: ((note: N) => void) | null;
  onDeleteNote?: ((note: N) => void) | null;
  /** Which notes show Delete when onDeleteNote is set; absent, every note does. */
  canDeleteNote?: (note: N) => boolean;
  /** Which notes show Edit when onEditNote is set; absent, every note does. */
  canEditNote?: (note: N) => boolean;
  /** An undated standing note after the dated ones (a person's free-text field). */
  standing?: ReactNode;
}

export function PeaceTeamNotes<N extends TeamNote = TeamNote>({ notes, providerName, onAddNote, onEditNote, onDeleteNote, canDeleteNote, canEditNote, standing }: PeaceTeamNotesProps<N>): ReactNode {
  return (
    <div className="rounded-card border border-warm-400/30 bg-sand px-6 py-[22px]">
      <div className="mb-3 flex items-center gap-2.5">
        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-base bg-sage-400">
          <HubBrandMark
            label=""
            className="flex w-[21px] [&>svg]:h-auto [&>svg]:w-full"
            style={{ color: 'var(--color-cream)' }}
          />
        </span>
        <span className="font-display text-base font-semibold text-sage-800">From the Peace team</span>
      </div>

      {notes.length === 0 && !standing ? (
        <p className="text-sm text-ink-soft">
          {onAddNote
            ? endSentence(`No notes yet. Be the first to share what you know about ${providerName}`)
            : endSentence(`No notes yet for ${providerName}`)}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {notes.map((note) => {
            const typeMeta = NOTE_TYPE_STYLES[note.note_type] ?? NOTE_TYPE_STYLES.general;
            const author = note.is_anonymous ? 'Anonymous' : note.author_display_name;
            return (
              <div key={note.id} className="border-t border-sage-800/[8%] pt-4 first:border-t-0 first:pt-0">
                <p className="whitespace-pre-wrap font-display text-sm italic leading-[1.55] text-ink">{note.body}</p>
                <div className="mt-2 flex items-center gap-2 text-xs text-ink-soft">
                  <span className={`rounded-tight px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${typeMeta.classes}`}>{typeMeta.label}</span>
                  <span>Added by {author ?? 'the Peace team'} · {formatRelative(note.created_at)}</span>
                  <span className="ml-auto flex items-center gap-1">
                    {onEditNote && (canEditNote === undefined || canEditNote(note)) && (
                      <button type="button" onClick={() => onEditNote(note)} aria-label="Edit note" title="Edit note" data-testid={`edit-note-${note.id}`} className="rounded px-1 leading-none text-sage-400 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
                        <Pencil size={13} aria-hidden="true" />
                      </button>
                    )}
                    {onDeleteNote && (canDeleteNote === undefined || canDeleteNote(note)) && (
                      <button type="button" onClick={() => onDeleteNote(note)} aria-label="Delete note" title="Delete note" data-testid={`delete-note-${note.id}`} className="rounded px-1 leading-none text-sage-400 hover:text-warm-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-500">
                        <Trash2 size={13} aria-hidden="true" />
                      </button>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
          {standing ? (
            <div data-testid="team-notes-standing" className="border-t border-sage-800/[8%] pt-4 first:border-t-0 first:pt-0">
              {standing}
            </div>
          ) : null}
        </div>
      )}

      {onAddNote && (
        <Button
          variant="secondary"
          className="mt-4"
          onClick={onAddNote}
          icon={<Pencil size={14} aria-hidden="true" />}
        >
          {notes.length === 0 ? 'Add a note' : 'Add another note'}
        </Button>
      )}
    </div>
  );
}
